import { useMemo, useState } from "react";
import { CONTENT_SLOT_CONTENT_TYPE_OPTIONS } from "@constants/content-slot-content-type-options";
import { DEFAULT_AXNUI_LAYOUT } from "@iddataweb/idw-common-ui/constants";
import { resources } from "@constants/hardcoded-strings";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { AXNUIFormContentSlotContentTypes, ThemingGenericContentSlotTypes } from "@iddataweb/idw-common-ui/enums";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ResourceCategoryTypes } from "@enums/resource-category-types";
import { AXNUILayout, AXNUILayoutHelpTextInner, AXNUILayoutHelpText, AXNUIContent } from "@iddataweb/idw-common-ui/types";
import { SelectOption } from "@idw-types/common";
import { ThemingGenericContentSlotValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors";
import { ContentPageBody } from "../../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../../common/content-page-header/content-page-header";
import { ThemingFormContainer } from "../../../../common/theming-form-container/theming-form-container";
import { ThemingConfigurationContainer } from "../../../../common/theming-configuration-container/theming-configuration-container";
import { ThemingSubHeaderContainer } from "../../../../common/theming-sub-header-container/theming-sub-header-container";
import { ThemingFormSection } from "../../../../common/theming-form-section/theming-form-section";
import { SortableFormContentSlotContentTypesContainer } from "../common/sortable-form-content-slot-content-types-container/sortable-form-content-slot-content-types-container";
import { GenericContentSlotContentTypeContainer } from "../common/generic-content-slot-content-type-container/generic-content-slot-content-type-container";
import { FormContentSlotContentTypeSelectContainer } from "../common/form-content-slot-content-type-select-container/form-content-slot-content-type-select-container";
import { updateHelpTextCustomHtmlVariablesIntoAXNUIContent } from "@utils/custom-html-and-css-util";

type ThemingPreFormContentSlotTabContainerProps = {
    isDisabled: boolean;
    validationErrors: ThemingGenericContentSlotValidationErrors;
    axnuiContent: AXNUIContent;
    axnuiLayout: AXNUILayout;
    predefinedHelpTextTypeOptions: Array<SelectOption>;
    generatePreviewComponent: (currentTab: AXNUIThemingTabTypes) => React.ReactNode;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    updateAXNUILayoutPropertyValue: (axnuiLayout: AXNUILayout) => void;
    updateAXNUIContentPropertyValue: (axnuiContent: AXNUIContent) => void;
    updateAXNUILayoutHtmlTextAndVariables: (axnuiLayout: AXNUILayout, axnuiContent: AXNUIContent, helpTextType: ThemingGenericContentSlotTypes) => void;
};

export const ThemingPreFormContentSlotTabContainer = (props: ThemingPreFormContentSlotTabContainerProps) => {
    const {
        isDisabled,
        validationErrors,
        axnuiContent,
        axnuiLayout,
        predefinedHelpTextTypeOptions,
        generatePreviewComponent,
        uploadFileToS3,
        updateAXNUILayoutPropertyValue,
        updateAXNUIContentPropertyValue,
        updateAXNUILayoutHtmlTextAndVariables
    } = props;

    const helpTextType = ThemingGenericContentSlotTypes.PRE_FORM_HELP_TEXT;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const order = useMemo(() => {
        return [...(axnuiLayout.helpText.preformHelpText.order || [])];
    }, [axnuiLayout?.helpText?.preformHelpText?.order]);

    const setOrder = (newOrder: Array<AXNUIFormContentSlotContentTypes>) => {
        updateAXNUILayoutPropertyValue({
            ...axnuiLayout,
            helpText: {
                ...(axnuiLayout.helpText || {}) as AXNUILayoutHelpText,
                preformHelpText: {
                    ...(axnuiLayout.helpText.preformHelpText || {}) as AXNUILayoutHelpTextInner,
                    order: newOrder
                }
            }
        });
    };

    const updateFormContentSlotContentField = (newValue: Partial<AXNUILayoutHelpTextInner>, htmlTextVariables?: Record<string, string>) => {
       const newAxnuiLayout = {
            ...axnuiLayout,
            helpText: {
                ...(axnuiLayout.helpText || {}) as AXNUILayoutHelpText,
                preformHelpText: {
                    ...(axnuiLayout.helpText.preformHelpText || {}) as AXNUILayoutHelpTextInner,
                    ...newValue
                }
            }
        };

        if(htmlTextVariables){
            const updatedAXNUIContent = updateHelpTextCustomHtmlVariablesIntoAXNUIContent(axnuiContent, htmlTextVariables, helpTextType);
            updateAXNUILayoutHtmlTextAndVariables(newAxnuiLayout, updatedAXNUIContent, helpTextType);
        } else {
            updateAXNUILayoutPropertyValue(newAxnuiLayout);
        }
    };
    
    const updateHtmlTextVariables = (htmlTextVariables: Record<string, string>) => {
        const updatedAXNUIContent = updateHelpTextCustomHtmlVariablesIntoAXNUIContent(axnuiContent, htmlTextVariables, helpTextType);
        updateAXNUIContentPropertyValue(updatedAXNUIContent);
    };

    const previewComponent = useMemo(() => {
        return generatePreviewComponent(AXNUIThemingTabTypes.PRE_FORM_CONTENT_SLOT);
    }, [generatePreviewComponent]);

    const mappedOrder = useMemo(() => {
        return order.map(contentType => CONTENT_SLOT_CONTENT_TYPE_OPTIONS.find(option => option.value === contentType)?.label).filter(x => x);
    }, [order]);

    const htmlTextVariables = useMemo(() => {
        return axnuiContent?.helpTextCustomHtmlVariables?.[helpTextType] || {};
    }, [axnuiContent.helpTextCustomHtmlVariables]);

    return (
        <div>
            <ContentPageHeader title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.preFormContentSlot.title} />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer description={resources.pages.verificationServiceDetails.tabs.theming.subTabs.preFormContentSlot.description} />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormSection showSeparator={order.length > 0}>
                            <FormContentSlotContentTypeSelectContainer
                                formContentSlotType={"pre-form-content-slot"}
                                isDisabled={isDisabled}
                                labelWidth={labelWidth}
                                layout={layout}
                                order={order}
                                setOrder={setOrder}
                            />
                        </ThemingFormSection>
                        {order.length > 0 && (
                            <>
                                <ThemingFormSection showSeparator={true} title={resources.pages.verificationServiceDetails.tabs.theming.subTabs.preFormContentSlot.sections.order.title}>
                                    <SortableFormContentSlotContentTypesContainer
                                        formContentSlotType={"pre-form-content-slot"}
                                        isDisabled={isDisabled}
                                        order={order}
                                        mappedOrder={mappedOrder}
                                        setOrder={setOrder}
                                    />
                                </ThemingFormSection>
                                {order.map((contentType, index) => (
                                    <ThemingFormSection key={`pre-form-content-slot-content-type-${contentType}`} showSeparator={order.length - 1 !== index} title={mappedOrder[index]}>
                                        <GenericContentSlotContentTypeContainer
                                            formContentSlotType={"pre form help text"}
                                            contentType={contentType}
                                            resourceCategoryType={ResourceCategoryTypes.PRE_FORM_CONTENT}
                                            isDisabled={isDisabled}
                                            labelWidth={labelWidth}
                                            layout={layout}
                                            validationErrors={validationErrors}
                                            predefinedHelpTextTypeOptions={predefinedHelpTextTypeOptions}
                                            defaultPredefinedFormHelpText={DEFAULT_AXNUI_LAYOUT.helpText.preformHelpText.formHelpTextType}
                                            selectedPredefinedFormHelpText={axnuiLayout.helpText?.preformHelpText?.formHelpTextType}
                                            values={axnuiLayout.helpText.preformHelpText}
                                            defaultValues={DEFAULT_AXNUI_LAYOUT.helpText.preformHelpText}
                                            defaultExplicitConsentMessage={null}
                                            explicitConsentMessage={null}
                                            htmlTextVariables={htmlTextVariables}
                                            uploadFileToS3={uploadFileToS3}
                                            updateFormContentSlotContentField={updateFormContentSlotContentField}
                                            updateAXNUIContentExplicitConsentMessage={() => {}}
                                            updateHtmlTextVariables={updateHtmlTextVariables}
                                        />
                                    </ThemingFormSection>
                                ))}
                            </>
                        )}
                    </ThemingFormContainer>
                    {previewComponent}
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};